import type { IMappedGeoMappingItem } from 'partner360/components/filters'

import type { I621Form } from '../../../api/types'
import type { IPartnerDocumentSection } from '../../../lib/documentsAndLinks'
import { buildGeoMappingTree } from '../../../lib/revenueAndSobHierarchy/geoMappingHierarchy'
import { normalizeGeoKey } from '../../../lib/revenueAndSobHierarchy/normalizeGeoKey'
import type { IDocumentGroup, IDocumentsNavigation } from '../types'

import { countDocuments } from './countDocuments'

interface IParams extends Pick<IDocumentsNavigation, 'grouping' | 'documentType' | 'geoPath'> {
  sections: IPartnerDocumentSection[]
  partners: I621Form[]
  geoMapping: IMappedGeoMappingItem[]
  readOnly: boolean
}
export const buildDocumentGroups = ({
  sections,
  grouping,
  documentType,
  geoPath,
  partners,
  geoMapping,
  readOnly,
}: IParams): IDocumentGroup[] => {
  const result = new Map<string, IDocumentGroup>()
  const { chainByCountry } = buildGeoMappingTree(geoMapping)
  const partnerById = new Map(partners.map((partner) => [partner.PartnerId.trim(), partner]))
  const add = (id: string, label: string, section: IPartnerDocumentSection) => {
    const group = result.get(id)
    if (group) {
      group.sections.push(section)
    } else {
      result.set(id, { id, label, sections: [section] })
    }
  }
  if (!documentType) {
    return []
  }
  sections.forEach((original) => {
    const section =
      documentType === 'elt' ? { ...original, qbrDocuments: [] } : { ...original, eltDocuments: [] }
    if (countDocuments([section]) === 0 && readOnly) {
      return
    }
    if (grouping === 'partner') {
      add(section.partnerId, section.partnerName, section)
      return
    }
    if (grouping === 'geo') {
      const partner = partnerById.get(section.partnerId)
      const chain = chainByCountry.get(normalizeGeoKey(partner?.CountryCode ?? '')) ?? []
      if (!geoPath.every((step, index) => chain[index]?.itemId === step.id)) {
        return
      }
      const node = chain[geoPath.length]
      add(
        node?.itemId ?? `unmapped:${normalizeGeoKey(partner?.CountryCode ?? '')}`,
        node?.name ?? partner?.Country ?? 'Unmapped',
        section
      )
      return
    }
    const creators = new Map<string, string>()
    ;[...section.eltDocuments, ...section.qbrDocuments].forEach((document) => {
      const name = document.CreatedBy?.trim() || 'Unknown creator'
      creators.set(name.toLowerCase(), name)
    })
    creators.forEach((label, id) => {
      const matches = (document: { CreatedBy?: string }) =>
        (document.CreatedBy?.trim() || 'Unknown creator').toLowerCase() === id
      add(id, label, {
        ...section,
        eltDocuments: section.eltDocuments.filter(matches),
        qbrDocuments: section.qbrDocuments.filter(matches),
      })
    })
  })
  return [...result.values()].sort((a, b) => a.label.localeCompare(b.label))
}
